import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  HStack,
  Stack,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  createLibraryRecord,
  getLibrariesData,
  updateLibrariesData,
} from "../../../services/Libraries";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import { getAllDocTypesData } from "../../../services/DocumentTypes";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import { getMasterListAliasSuggestions } from "../../../services/masterlistAlias";
import ImagePreview from "../../../components/ImagePreview";
import { useSelector } from "react-redux";
import FormLoader from "../../../CommonComponents/FormLoader";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";

// Left column schema
const leftColumnSchema = (alias) => ({
  title: "",
  type: "object",
  required: ["alias", "title", "document_type", "masterlist_alias"],
  properties: {
    _id: { type: "string" },
    isVisible: {
      type: "boolean",
      title: "Is Visible",
    },
    alias: {
      type: "string",
      title: "Alias",
    },
    masterlist_alias: {
      type: "string",
      title: "Masterlist Alias",
    },
    source_alias: {
      type: "string",
      title: "Source Alias",
    },
    source_alias2: {
      type: "string",
      title: "Source Alias 2",
    },
    source_alias3: {
      type: "string",
      title: "Source Alias 3",
    },
    subject: {
      type: "string",
      title: "Subject",
    },
    document_type: {
      type: "string",
      title: "Document type",
      default: "",
    },
    document_type_in_full: {
      type: "string",
      title: "Document Type in Full",
      default: "",
    },
    file_name: {
      type: "string",
      title: "File Name",
    },
    ...(!!alias
      ? {
          wnn: {
            type: "string",
            title: "WNN",
            default: "",
          },
        }
      : {}),
    title: {
      type: "string",
      title: "Title",
    },
    sub_title: {
      type: "string",
      title: "Sub Title",
    },
    cover_image: {
      type: "string",
      title: "Cover Image",
    },
    preview_cover_image: {
      type: "string",
      title: "Image URL",
    },
  },
});

// Right column schema
const rightColumnSchema = () => ({
  title: "",
  type: "object",
  properties: {
    location_of_publication_in_tris: {
      type: "string",
      title: "Location of Publication in Tris",
    },
    accession_number: {
      type: "string",
      title: "Accession Number",
    },
    actual_date: {
      type: "string",
      title: "Actual Date",
    },
    back_cover_image: {
      type: "string",
      title: "Back Cover Image",
    },
    preview_back_cover_image: {
      type: "string",
      title: "Image URL",
    },
    direct_url_of_cover_image: {
      type: "string",
      title: "Direct url of cover images",
    },
    preview_direct_url_of_cover_image: {
      type: "string",
      title: "Image URL",
    },
    can_enlarge: {
      type: "boolean",
      title: "Image Enlargement",
    },
    text_file: {
      type: "string",
      title: "Text File",
    },
    external_notes: {
      type: "string",
      title: "External Notes",
    },
    cdt_release_type: {
      type: "string",
      title: "CDT Release Type",
    },
    corrected_sequence: {
      type: "string",
      title: "Corrected Sequence",
    },
    details_page: {
      type: "string",
      title: "Details Page",
    },
    external_source: {
      type: "string",
      title: "External Source",
    },
    cine_object_title: {
      type: "string",
      title: "Cine Object Title",
    },
    cine_object_description: {
      type: "string",
      title: "Cine Object Description",
    },
    cine_object_actual_date: {
      type: "string",
      title: "Cine Object Actual Date",
    },
  },
});

// Combined schema for preview mode
const combinedSchema = (alias) => ({
  title: "Quadrant-1 fields",
  description: "",
  type: "object",
  required: ["alias", "title", "document_type", "masterlist_alias"],
  properties: {
    _id: { type: "string" },
    isVisible: {
      type: "boolean",
      title: "Is Visible",
    },
    alias: {
      type: "string",
      title: "Alias",
    },
    masterlist_alias: {
      type: "string",
      title: "Masterlist Alias",
    },
    source_alias: {
      type: "string",
      title: "Source Alias",
    },
    source_alias2: {
      type: "string",
      title: "Source Alias 2",
    },
    source_alias3: {
      type: "string",
      title: "Source Alias 3",
    },
    subject: {
      type: "string",
      title: "Subject",
    },
    document_type: {
      type: "string",
      title: "Document type",
      default: "",
    },
    document_type_in_full: {
      type: "string",
      title: "Document Type in Full",
      default: "",
    },
    file_name: {
      type: "string",
      title: "File Name",
    },
    ...(!!alias
      ? {
          wnn: {
            type: "string",
            title: "WNN",
            default: "",
          },
        }
      : {}),
    title: {
      type: "string",
      title: "Title",
    },
    sub_title: {
      type: "string",
      title: "Sub Title",
    },
    location_of_publication_in_tris: {
      type: "string",
      title: "Location of Publication in Tris",
    },
    accession_number: {
      type: "string",
      title: "Accession Number",
    },
    actual_date: {
      type: "string",
      title: "Actual Date",
    },
    cover_image: {
      type: "string",
      title: "Cover Image",
    },
    preview_cover_image: {
      type: "string",
      title: "Image URL",
    },
    back_cover_image: {
      type: "string",
      title: "Back Cover Image",
    },
    preview_back_cover_image: {
      type: "string",
      title: "Image URL",
    },
    direct_url_of_cover_image: {
      type: "string",
      title: "Direct url of cover images",
    },
    preview_direct_url_of_cover_image: {
      type: "string",
      title: "Image URL",
    },
    can_enlarge: {
      type: "boolean",
      title: "Image Enlargement",
    },
    text_file: {
      type: "string",
      title: "Text File",
    },
    external_notes: {
      type: "string",
      title: "External Notes",
    },
    cdt_release_type: {
      type: "string",
      title: "CDT Release Type",
    },
    corrected_sequence: {
      type: "string",
      title: "Corrected Sequence",
    },
    details_page: {
      type: "string",
      title: "Details Page",
    },
    external_source: {
      type: "string",
      title: "External Source",
    },
    cine_object_title: {
      type: "string",
      title: "Cine Object Title",
    },
    cine_object_description: {
      type: "string",
      title: "Cine Object Description",
    },
    cine_object_actual_date: {
      type: "string",
      title: "Cine Object Actual Date",
    },
  },
});

// Left column UI schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": { norender: true },
  _id: { "ui:widget": "hidden" },
  "ui:style": {
    fontSize: "14px",
  },
  preview_cover_image: { "ui:widget": ImageWidget },
  alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
  masterlist_alias: {
    "ui:emptyValue": "",
    "ui:widget": "masterlistAliasTypeahead",
  },
  source_alias: { "ui:emptyValue": "" },
  source_alias2: { "ui:emptyValue": "" },
  source_alias3: { "ui:emptyValue": "" },
  subject: { "ui:emptyValue": "" },
  document_type: { "ui:emptyValue": "", "ui:widget": "typeahead" },
  document_type_in_full: { "ui:emptyValue": "" },
  file_name: { "ui:emptyValue": "", "ui:readonly": true },
  ...(alias
    ? {
        wnn: {
          "ui:emptyValue": "",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      }
    : {}),
  title: { "ui:emptyValue": "" },
  sub_title: { "ui:emptyValue": "" },
  cover_image: { "ui:emptyValue": "" },
});

// Right column UI schema
const rightColumnUiSchema = (isSubmitting) => ({
  "ui:submitButtonOptions": { norender: true },
  "ui:style": {
    fontSize: "14px",
  },
  preview_back_cover_image: { "ui:widget": ImageWidget },
  preview_direct_url_of_cover_image: { "ui:widget": ImageWidget },
  location_of_publication_in_tris: { "ui:widget": "hidden" },
  accession_number: { "ui:emptyValue": "" },
  actual_date: { "ui:emptyValue": "" },
  back_cover_image: { "ui:emptyValue": "" },
  direct_url_of_cover_image: { "ui:emptyValue": "" },
  can_enlarge: { "ui:emptyValue": "" },
  text_file: { "ui:emptyValue": "" },
  external_notes: { "ui:emptyValue": "" },
  cdt_release_type: { "ui:widget": "hidden" },
  corrected_sequence: { "ui:emptyValue": "" },
  details_page: { "ui:emptyValue": "" },
  external_source: { "ui:emptyValue": "" },
  cine_object_title: { "ui:widget": "hidden" },
  cine_object_description: { "ui:widget": "hidden" },
  cine_object_actual_date: { "ui:widget": "hidden" },
});

// Combined UI schema for preview mode
const combinedUiSchema = (alias, isSubmitting) => ({
  "ui:submitButtonOptions": { norender: true },
  _id: { "ui:widget": "hidden" },
  "ui:style": {
    fontSize: "14px",
  },
  preview_cover_image: { "ui:widget": ImageWidget },
  preview_back_cover_image: { "ui:widget": ImageWidget },
  preview_direct_url_of_cover_image: { "ui:widget": ImageWidget },
  alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
  masterlist_alias: {
    "ui:emptyValue": "",
    "ui:widget": "masterlistAliasTypeahead",
  },
  source_alias: { "ui:emptyValue": "" },
  source_alias2: { "ui:emptyValue": "" },
  source_alias3: { "ui:emptyValue": "" },
  subject: { "ui:emptyValue": "" },
  document_type: { "ui:emptyValue": "", "ui:widget": "typeahead" },
  document_type_in_full: { "ui:emptyValue": "" },
  file_name: { "ui:emptyValue": "", "ui:readonly": true },
  ...(alias
    ? {
        wnn: {
          "ui:emptyValue": "",
          "ui:readonly": true,
          "ui:disabled": true,
        },
      }
    : {}),
  title: { "ui:emptyValue": "" },
  sub_title: { "ui:emptyValue": "" },
  location_of_publication_in_tris: { "ui:widget": "hidden" },
  accession_number: { "ui:emptyValue": "" },
  actual_date: { "ui:emptyValue": "" },
  cover_image: { "ui:emptyValue": "" },
  back_cover_image: { "ui:emptyValue": "" },
  direct_url_of_cover_image: { "ui:emptyValue": "" },
  can_enlarge: { "ui:emptyValue": "" },
  text_file: { "ui:emptyValue": "" },
  external_notes: { "ui:emptyValue": "" },
  cdt_release_type: { "ui:widget": "hidden" },
  corrected_sequence: { "ui:emptyValue": "" },
  details_page: { "ui:emptyValue": "" },
  external_source: { "ui:emptyValue": "" },
  cine_object_title: { "ui:widget": "hidden" },
  cine_object_description: { "ui:widget": "hidden" },
  cine_object_actual_date: { "ui:widget": "hidden" },
});

const Section1 = () => {
  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  const navigate = useNavigate();

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "isVisible",
      "alias",
      "masterlist_alias",
      "source_alias",
      "source_alias2",
      "source_alias3",
      "subject",
      "document_type",
      "document_type_in_full",
      "file_name",
      "wnn",
      "title",
      "sub_title",
      "cover_image",
      "preview_cover_image",
    ];
    const rightFields = [
      "location_of_publication_in_tris",
      "accession_number",
      "actual_date",
      "back_cover_image",
      "preview_back_cover_image",
      "direct_url_of_cover_image",
      "preview_direct_url_of_cover_image",
      "can_enlarge",
      "text_file",
      "external_notes",
      "cdt_release_type",
      "corrected_sequence",
      "details_page",
      "external_source",
      "cine_object_title",
      "cine_object_description",
      "cine_object_actual_date",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  const dataWithImage = (data) => ({
    ...data,
    preview_cover_image: data?.cover_image,
    preview_back_cover_image: data?.back_cover_image,
    preview_direct_url_of_cover_image: data?.direct_url_of_cover_image,
  });
  const type = window.location.pathname.split("/")[1];
  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          setIsLoading(true);
          const result = await getLibrariesData(alias, "section-1", type);
          const fetchedDocTypes = await loadDocTypes();
          if (result) {
            // Support both array response and { data: [record] } shape
            const rawRecord = Array.isArray(result)
              ? result[0]
              : result?.data?.[0];

            // To Show image on form need to provide url to image field which is only used to show the image
            const updatedDataWithImage = dataWithImage(rawRecord);

            // Set document_type_in_full: prefer lookup by document_type, then keep API value
            const lookedUpFull =
              rawRecord?.document_type && Array.isArray(fetchedDocTypes)
                ? fetchedDocTypes.find(
                    (item) => item?.alias === rawRecord.document_type,
                  )?.name
                : undefined;
            const processedDataWithDocType = {
              ...updatedDataWithImage,
              document_type_in_full:
                lookedUpFull ??
                updatedDataWithImage.document_type_in_full ??
                "",
            };

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(
              processedDataWithDocType,
            );
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(processedDataWithDocType);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateLibrariesData(
          alias,
          finalFormData,
          "",
          "section-1",
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      } else {
        const result = await createLibraryRecord(finalFormData); // Call the create API
        if (result?.status === 201) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "New library object created successfully");
          navigate(`/libraries/${result?.data?.alias}`); // Navigate to the newly created post
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectedDocType = async (selectedOption) => {
    const fetchedDocTypes = await loadDocTypes();
    if (selectedOption) {
      const docTypeFull =
        fetchedDocTypes.find((item) => item?.alias === selectedOption)?.name ||
        "";

      // Update both left form data and combined form data
      setLeftFormData((prev) => ({
        ...prev,
        document_type: selectedOption,
        document_type_in_full: docTypeFull,
      }));

      setCombinedFormData((prev) => ({
        ...prev,
        document_type: selectedOption,
        document_type_in_full: docTypeFull,
      }));
    }
  };

  const loadDocTypes = async () => {
    try {
      const result = await getAllDocTypesData();
      // Support both { data: [...] } and { data: { data: [...] } } response shapes
      const list = result?.data?.data ?? result?.data;
      return Array.isArray(list) ? list : [];
    } catch (error) {
      showErrorToast(toast, "Failed to load document types");
    }
  };

  const fetchDocTypes = async (inputValue, keyForLabelValue = "alias") => {
    const fetchedDocTypes = await loadDocTypes();
    const transformedOptions = fetchedDocTypes.map((item) => ({
      label: item[keyForLabelValue] || "",
      value: item[keyForLabelValue] || "",
    }));

    return transformedOptions.filter((option) =>
      option?.label?.toLowerCase()?.includes(inputValue?.toLowerCase()),
    );
  };

  const loadMasterlistAliasSuggestions = async (inputValue) => {
    try {
      const result = await getMasterListAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
      showErrorToast(toast, "Failed to load document types");
    }
  };

  const fetchMasterlistAliasSuggestions = async (inputValue) => {
    const fetchedMasterListAliasSuggestions =
      await loadMasterlistAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (
      !inputValue ||
      typeof inputValue !== "string" ||
      !fetchedMasterListAliasSuggestions
    ) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias,
    }));
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchDocTypes(inputValue, "alias")}
        handleChangeCB={handleSelectedDocType}
      />
    ),
    masterlistAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchMasterlistAliasSuggestions}
      />
    ),
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Library Quadrant-1 Fields
                  </Box>
                  {combinedFormData?.preview_cover_image &&
                    combinedFormData.preview_cover_image.trim() !== "" && (
                      <IconButton
                        aria-label={
                          showImagePreview
                            ? "Hide image preview"
                            : "Show image preview"
                        }
                        icon={showImagePreview ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        colorScheme={showImagePreview ? "red" : "blue"}
                        onClick={() => setShowImagePreview((prev) => !prev)}
                        title={
                          showImagePreview
                            ? "Close image preview"
                            : "Open image preview"
                        }
                      />
                    )}
                </HStack>
                <Form
                  schema={combinedSchema(alias)}
                  uiSchema={combinedUiSchema(alias, isSubmitting)}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
              {/* Third column for image preview when clicked */}
              {showImagePreview &&
                combinedFormData?.preview_cover_image &&
                combinedFormData.preview_cover_image.trim() !== "" && (
                  <HStack
                    sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                  >
                    <ImagePreview image={combinedFormData?.cover_image} />
                  </HStack>
                )}
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Library Quadrant-1 Fields
                      </Box>
                      {leftFormData?.preview_cover_image &&
                        leftFormData.preview_cover_image.trim() !== "" && (
                          <IconButton
                            aria-label={
                              showImagePreview
                                ? "Hide image preview"
                                : "Show image preview"
                            }
                            icon={
                              showImagePreview ? <ViewOffIcon /> : <ViewIcon />
                            }
                            size="sm"
                            colorScheme={showImagePreview ? "red" : "blue"}
                            onClick={() => setShowImagePreview((prev) => !prev)}
                            title={
                              showImagePreview
                                ? "Close image preview"
                                : "Open image preview"
                            }
                          />
                        )}
                    </HStack>
                    <Form
                      schema={leftColumnSchema(alias)}
                      uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema()}
                      uiSchema={rightColumnUiSchema(isSubmitting)}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
              {/* Third column for image preview when clicked */}
              {showImagePreview &&
                leftFormData?.preview_cover_image &&
                leftFormData.preview_cover_image.trim() !== "" && (
                  <HStack
                    sx={{ position: "sticky", top: "10px", marginLeft: "0px" }}
                  >
                    <ImagePreview image={leftFormData?.cover_image} />
                  </HStack>
                )}
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section1;
